/**
 * ProjectDailyTasksSection.jsx — Phase 84.63 rewrite: the gated "Board"
 * Phase 101 — Master Task Engine: List/Kanban parity, mandatory Start/Due
 * date, Importance, logged-hours chip and a separate Abandoned strip.
 *
 * Kanban board of daily tasks linked to one project.
 *   To do · In progress · Review · Done  (+ Abandoned, shown separately)
 *
 * Gating: any team member can move a task INTO Review. Only the Founder or
 * this project's Lead (team_members[].project_role === "lead", set via the
 * project wizard) can move a task from Review (or anywhere) to Done — the
 * server enforces this with a 403; the UI also hides the "→ Done" quick-move
 * button for everyone else so it's not a dead-end click.
 *
 * A task with no activity for 14 days auto-flips to Abandoned (server-side
 * cron); anyone with edit rights can revive it back to To do.
 *
 * RBAC mirrors the dashboard widget:
 *   - Anyone with project access can add their own task.
 *   - Only admin / client_manager can assign to others.
 *   - Only the assignee, creator, or admin can change status / log time.
 *   - Only the creator or admin can delete.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import usePermissions from "@/lib/usePermissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  ListChecks, Plus, X, Trash2, Loader2, CheckSquare, Pencil, Clock, AlertTriangle,
  ChevronDown, ChevronRight, Crown, List as ListIcon, LayoutGrid, RotateCcw, History,
} from "lucide-react";
import { DAILY_TASK_STATUSES, ABANDONED_STATUS, STATUS_LABEL, formatLoggedHours, dueChip, isReviewStale } from "@/lib/dailyTaskStatus";
import linkify from "@/lib/linkify";

const COLUMN_STYLES = {
  planned:     { header: "text-slate-600",   accent: "border-slate-200"   },
  in_progress: { header: "text-blue-700",    accent: "border-blue-200"    },
  review:      { header: "text-purple-700",  accent: "border-purple-200"  },
  done:        { header: "text-emerald-700", accent: "border-emerald-200" },
};
const IMPORTANCE_TONE = {
  high: "border-red-300 text-red-700 bg-red-50",
  medium: "border-amber-300 text-amber-700 bg-amber-50",
  low: "border-slate-300 text-slate-600 bg-slate-50",
};
const VIEW_KANBAN = "kanban";
const VIEW_LIST = "list";
const todayIso = () => new Date().toISOString().slice(0, 10);
const plusDaysIso = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

export default function ProjectDailyTasksSection({ projectId, project }) {
  const { user } = useAuth();
  const can = usePermissions();
  const isAdmin = can("tasks_admin");
  // Phase 84.2 — founder is admin superset (Phase 79 parity).
  const canAssignOthers = can("tasks_admin") || can("tasks_assign");
  // Phase 84.63 — Founder, anyone granted tasks_move_to_done/tasks_admin
  // via Access Management, or this project's own assigned Lead can move a
  // task to Done (mirrors backend's _user_is_project_lead_or_founder).
  const isLeadOrFounder = isAdmin || can("tasks_move_to_done") ||
    (project?.team_members || []).some((m) => m.user_id === user?.id && m.project_role === "lead");

  const [tasks, setTasks] = useState(null);
  const [team, setTeam] = useState([]);
  const [projects, setProjects] = useState([]);  // Phase 84.2 — for cross-project moves in the edit dialog
  const [composing, setComposing] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("projectBoardView") === VIEW_LIST ? VIEW_LIST : VIEW_KANBAN; }
    catch { return VIEW_KANBAN; }
  });
  useEffect(() => { try { localStorage.setItem("projectBoardView", view); } catch { /* ignore */ } }, [view]);
  const [abandonedOpen, setAbandonedOpen] = useState(false);

  const loadTeamAndProjects = useCallback(async () => {
    // Fetched once and reused by both the composer and the per-card edit
    // dialog. Both endpoints are cheap and safe for every internal role.
    if (team.length === 0) {
      try {
        const { data } = await api.get("/users?kind=internal");
        setTeam((data || []).filter((u) => u.id !== user?.id));
      } catch { /* fall through */ }
    }
    if (projects.length === 0) {
      try {
        const { data } = await api.get("/daily-tasks/projects");
        setProjects(data || []);
      } catch { /* fall through */ }
    }
  }, [team.length, projects.length, user?.id]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/daily-tasks?assignee_id=all&project_id=${projectId}`);
      setTasks(data || []);
    } catch { setTasks([]); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!composing || team.length > 0) return;
    loadTeamAndProjects();
  }, [composing, team.length, loadTeamAndProjects]);

  const activeTasks = useMemo(() => (tasks || []).filter((t) => t.status !== ABANDONED_STATUS), [tasks]);
  const abandonedTasks = useMemo(() => (tasks || []).filter((t) => t.status === ABANDONED_STATUS), [tasks]);

  const columns = useMemo(() => {
    const g = { planned: [], in_progress: [], review: [], done: [] };
    for (const t of activeTasks) {
      const s = DAILY_TASK_STATUSES.includes(t.status) ? t.status : "planned";
      g[s].push(t);
    }
    return g;
  }, [activeTasks]);

  const overdueCount = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    return activeTasks.filter((t) => t.status !== "done" && t.due_date && new Date(`${t.due_date}T00:00:00`) < today).length;
  }, [activeTasks]);

  const doneCount = activeTasks.filter((t) => t.status === "done").length;
  const totalCount = activeTasks.length;

  const setStatus = async (t, next, extra = {}) => {
    if (t.status === next) return;
    // Phase 101 — bouncing a task OUT of Review (to anything but Done) asks
    // for feedback so the assignee sees what to change; kept in the task's
    // nested review history.
    let review_feedback;
    if (t.status === "review" && next !== "done" && next !== "review") {
      review_feedback = window.prompt("What should change before this gets resubmitted for review?") || "";
    }
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, status: next } : x));
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, { status: next, review_feedback, ...extra });
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/daily-tasks/${t.id}`);
      setTasks((arr) => arr.filter((x) => x.id !== t.id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const patchTask = async (t, patch) => {
    try {
      const { data } = await api.patch(`/daily-tasks/${t.id}`, patch);
      setTasks((arr) => arr.map((x) => x.id === t.id ? data : x));
    } catch (e) { toast.error(formatApiError(e)); load(); }
  };

  const create = async (payload) => {
    try {
      const { data } = await api.post("/daily-tasks", { ...payload, project_id: projectId });
      setTasks((arr) => [...(arr || []), data]);
      setComposing(false);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!tasks) return null;

  return (
    <div className="card-flat overflow-hidden mt-6" data-testid="project-daily-tasks-section">
      {/* Header */}
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ListChecks size={14} className="text-[#F54800]" />
          <div className="font-display font-semibold text-slate-900 text-sm">Board</div>
          <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded-sm" data-testid="project-daily-tasks-count">
            {totalCount} total
            {doneCount > 0 && <> · <span className="text-emerald-600">{doneCount} done</span></>}
            {overdueCount > 0 && <> · <span className="text-red-600">{overdueCount} overdue</span></>}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="inline-flex items-center gap-0.5 border border-slate-200 rounded-sm p-0.5" data-testid="project-board-view-toggle">
            <button
              onClick={() => setView(VIEW_KANBAN)}
              className={`p-1.5 rounded-sm text-[11px] inline-flex items-center gap-1 ${view === VIEW_KANBAN ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="project-board-view-kanban" title="Kanban view"
            >
              <LayoutGrid size={11} />
            </button>
            <button
              onClick={() => setView(VIEW_LIST)}
              className={`p-1.5 rounded-sm text-[11px] inline-flex items-center gap-1 ${view === VIEW_LIST ? "bg-slate-900 text-white" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="project-board-view-list" title="List view"
            >
              <ListIcon size={11} />
            </button>
          </div>
          <Button
            size="sm"
            onClick={() => setComposing(true)}
            className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
            data-testid="project-daily-tasks-add-btn"
          >
            <Plus size={12} className="mr-1" /> Add task
          </Button>
        </div>
      </div>

      {/* Composer */}
      {composing && (
        <TaskComposer
          canAssignOthers={canAssignOthers}
          team={team}
          onCancel={() => setComposing(false)}
          onCreate={create}
        />
      )}

      {view === VIEW_KANBAN ? (
        <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 bg-slate-50/40" data-testid="project-kanban">
          {DAILY_TASK_STATUSES.map((s) => (
            <KanbanColumn
              key={s}
              status={s}
              tasks={columns[s]}
              currentUserId={user?.id}
              isAdmin={isAdmin}
              canAssignOthers={canAssignOthers}
              isLeadOrFounder={isLeadOrFounder}
              team={team}
              projects={projects}
              onEnsureLookups={loadTeamAndProjects}
              onSetStatus={setStatus}
              onDelete={remove}
              onPatch={patchTask}
            />
          ))}
        </div>
      ) : (
        <BoardListView
          tasks={activeTasks}
          currentUserId={user?.id}
          isAdmin={isAdmin}
          canAssignOthers={canAssignOthers}
          isLeadOrFounder={isLeadOrFounder}
          onSetStatus={setStatus}
          onDelete={remove}
        />
      )}

      {activeTasks.length === 0 && (
        <div className="px-5 pb-6 text-xs text-slate-400 italic text-center" data-testid="project-daily-tasks-empty">
          No tasks yet. Click <span className="text-slate-600 font-medium">Add task</span> above to start the board.
        </div>
      )}

      {/* Phase 101 — Abandoned strip: separate from the active board/counters,
          collapsed by default. Anyone with edit rights can revive a task. */}
      {abandonedTasks.length > 0 && (
        <div className="border-t border-slate-100 bg-red-50/30" data-testid="project-abandoned-strip">
          <button
            type="button"
            onClick={() => setAbandonedOpen((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-2.5 text-left hover:bg-red-50/60"
            data-testid="project-abandoned-toggle"
          >
            <span className="text-xs font-semibold text-red-700 inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Abandoned ({abandonedTasks.length}) — no activity in 14+ days
            </span>
            {abandonedOpen ? <ChevronDown size={13} className="text-red-500" /> : <ChevronRight size={13} className="text-red-500" />}
          </button>
          {abandonedOpen && (
            <div className="px-5 pb-4 space-y-2">
              {abandonedTasks.map((t) => (
                <div key={t.id} className="bg-white border border-red-200 rounded-sm p-2.5 flex items-center gap-2" data-testid={`abandoned-card-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-700 truncate">{t.title}</div>
                    <div className="text-[10px] text-slate-400">{t.assignee_name || "Unassigned"} · was {STATUS_LABEL[t.previous_status] || "To do"}</div>
                  </div>
                  <button
                    onClick={() => setStatus(t, t.previous_status || "planned")}
                    className="text-[11px] px-2 py-1 rounded-sm bg-slate-900 text-white hover:bg-slate-700 inline-flex items-center gap-1 shrink-0"
                    data-testid={`abandoned-revive-${t.id}`}
                  >
                    <RotateCcw size={10} /> Revive
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
function TaskComposer({ canAssignOthers, team, onCancel, onCreate }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState(plusDaysIso(7));
  const [importance, setImportance] = useState("medium");
  const [assignee, setAssignee] = useState("");
  const [critical, setCritical] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) return;
    if (!startDate || !dueDate) { toast.error("Start date and Due date are required"); return; }
    setBusy(true);
    const body = { title: title.trim(), start_date: startDate, due_date: dueDate, importance };
    if (description.trim()) body.description = description.trim();
    if (assignee) body.assignee_id = assignee;
    if (critical) body.critical = true;
    await onCreate(body);
    setBusy(false);
  };
  return (
    <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/60 space-y-2" data-testid="project-daily-tasks-composer">
      <Input
        autoFocus
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Task title (what needs to get done?)"
        className="h-8 rounded-sm text-sm"
        data-testid="project-daily-tasks-new-title"
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        rows={2}
        placeholder="Description (context, links, notes)…"
        className="rounded-sm text-sm"
        data-testid="project-daily-tasks-new-desc"
      />
      <div className="flex items-center gap-1.5 flex-wrap">
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Start</label>
        <Input
          type="date"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          className="h-8 rounded-sm text-sm w-[150px]"
          data-testid="project-daily-tasks-new-start"
        />
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">Due</label>
        <Input
          type="date"
          value={dueDate}
          onChange={(e) => setDueDate(e.target.value)}
          className="h-8 rounded-sm text-sm w-[150px]"
          data-testid="project-daily-tasks-new-due"
        />
        <select
          value={importance}
          onChange={(e) => setImportance(e.target.value)}
          className={`h-8 text-xs rounded-sm border px-2 font-medium ${IMPORTANCE_TONE[importance]}`}
          data-testid="project-daily-tasks-new-importance"
          title="Importance"
        >
          <option value="high">High</option>
          <option value="medium">Mid</option>
          <option value="low">Low</option>
        </select>
        {canAssignOthers && (
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            className="h-8 text-sm rounded-sm border border-slate-200 px-2 bg-white max-w-[200px]"
            data-testid="project-daily-tasks-new-assignee"
            title="Assign to (defaults to yourself)"
          >
            <option value="">Self</option>
            {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        )}
        <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600" data-testid="project-daily-tasks-new-critical">
          <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} className="accent-[#F54800]" />
          Critical (P0)
        </label>
        <div className="flex-1" />
        <Button size="sm" variant="outline" onClick={onCancel} className="rounded-sm h-8" data-testid="project-daily-tasks-cancel-btn">
          <X size={12} className="mr-1" /> Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={busy || !title.trim() || !startDate || !dueDate}
          className="rounded-sm h-8 bg-[#F54800] hover:bg-[#D63E00] text-white"
          data-testid="project-daily-tasks-submit-btn"
        >
          {busy ? <Loader2 size={11} className="mr-1 animate-spin" /> : <Plus size={11} className="mr-1" />} Add task
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
function KanbanColumn({ status, tasks, currentUserId, isAdmin, canAssignOthers, isLeadOrFounder, team, projects, onEnsureLookups, onSetStatus, onDelete, onPatch }) {
  const styles = COLUMN_STYLES[status];
  // Feb 2026 · Global Fixes P2 — the Done column is now collapsed by default
  // (an accordion) so historical archives never blow the page height. The
  // other three columns render everything (they rarely exceed a few dozen
  // rows in practice). When expanded, Done reveals in 50-row chunks.
  const isDone = status === "done";
  const CHUNK = 50;
  const [expanded, setExpanded] = useState(!isDone);
  const [visibleCount, setVisibleCount] = useState(isDone ? CHUNK : Infinity);
  useEffect(() => {
    setExpanded(!isDone);
    setVisibleCount(isDone ? CHUNK : Infinity);
  }, [status, isDone]);
  const capped = Number.isFinite(visibleCount) && tasks.length > visibleCount;
  const shown = expanded ? (capped ? tasks.slice(0, visibleCount) : tasks) : [];
  const hidden = tasks.length - shown.length;
  return (
    <div className="flex flex-col min-w-0" data-testid={`kanban-column-${status}`}>
      <button
        type="button"
        onClick={isDone ? () => setExpanded((v) => !v) : undefined}
        className={`flex items-center justify-between px-2 py-1.5 mb-2 w-full text-left ${isDone ? "hover:bg-slate-50 rounded-sm cursor-pointer" : "cursor-default"}`}
        data-testid={`kanban-column-${status}-toggle`}
        aria-expanded={expanded}
      >
        <div className={`label-overline ${styles.header} flex items-center gap-1.5`}>
          {isDone ? (expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}
          {STATUS_LABEL[status]}
          {status === "done" && <Crown size={10} className="text-amber-500" title="Only Founder / project Lead can move tasks here" />}
        </div>
        <span className="text-[10px] text-slate-500 bg-white border border-slate-200 rounded-sm px-1.5 py-0.5" data-testid={`kanban-count-${status}`}>
          {tasks.length}
        </span>
      </button>
      {expanded && (
        <div className="space-y-2 min-h-[80px]">
          {shown.map((t) => (
            <KanbanCard
              key={t.id}
              task={t}
              columnStatus={status}
              currentUserId={currentUserId}
              isAdmin={isAdmin}
              canAssignOthers={canAssignOthers}
              isLeadOrFounder={isLeadOrFounder}
              team={team}
              projects={projects}
              onEnsureLookups={onEnsureLookups}
              onSetStatus={onSetStatus}
              onDelete={onDelete}
              onPatch={onPatch}
            />
          ))}
          {tasks.length === 0 && (
            <div className="text-[11px] text-slate-400 italic px-2 py-3 text-center border border-dashed border-slate-200 rounded-sm bg-white/50">
              Empty
            </div>
          )}
          {hidden > 0 && (
            <button
              type="button"
              onClick={() => setVisibleCount((n) => (Number.isFinite(n) ? n + CHUNK : n))}
              className="w-full text-[11px] text-slate-500 hover:text-[#F54800] py-2 border border-dashed border-slate-200 rounded-sm bg-white/50 transition-colors"
              data-testid={`kanban-column-${status}-load-more`}
            >
              Show {Math.min(hidden, CHUNK)} more ({hidden} hidden)
            </button>
          )}
        </div>
      )}
      {!expanded && isDone && tasks.length > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-[11px] text-slate-500 hover:text-[#F54800] py-2 border border-dashed border-slate-200 rounded-sm bg-white/50 transition-colors"
          data-testid={`kanban-column-${status}-expand`}
        >
          Show {tasks.length} done task{tasks.length === 1 ? "" : "s"}
        </button>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
function ReviewHistoryBadge({ history, taskId }) {
  const rounds = history || [];
  if (rounds.length === 0) return null;
  const lastFeedback = [...rounds].reverse().find((r) => r.feedback)?.feedback;
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded-sm bg-purple-50 text-purple-700 border border-purple-200"
      title={lastFeedback ? `Last feedback: ${lastFeedback}` : `Round ${rounds.length}`}
      data-testid={`review-history-badge-${taskId}`}
    >
      <History size={8} /> R{rounds.length}
    </span>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
function KanbanCard({ task: t, columnStatus, currentUserId, isAdmin, canAssignOthers, isLeadOrFounder, team, projects, onEnsureLookups, onSetStatus, onDelete, onPatch }) {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(t.title);
  const [description, setDescription] = useState(t.description || "");
  const [startDate, setStartDate] = useState(t.start_date || "");
  const [dueDate, setDueDate] = useState(t.due_date || "");
  const [importance, setImportance] = useState(t.importance || "medium");
  const [projectId, setProjectId] = useState(t.project_id || "");                      // Phase 84.2
  const [assigneeId, setAssigneeId] = useState(t.assignee_id || "");                    // Phase 84.2
  const [critical, setCritical] = useState(!!t.critical);                               // Phase 84.62

  const canInteract = t.assignee_id === currentUserId || t.created_by === currentUserId || isAdmin;
  const canRemove = t.created_by === currentUserId || isAdmin;
  const chip = dueChip(t.due_date, t.status);
  const done = t.status === "done";
  const stale = columnStatus === "review" && isReviewStale(t);

  // Phase 84.63 — Review→Done is gated: only the Founder or this project's
  // Lead can move a task INTO "done". Everyone else keeps the other 3 quick
  // moves so the button never leads to a dead-end 403.
  const otherStatuses = DAILY_TASK_STATUSES.filter((s) => s !== columnStatus && (s !== "done" || isLeadOrFounder));

  const openEditor = () => {
    setEditing(true);
    onEnsureLookups?.();  // lazy-load team + project lists on first open
  };

  const saveEdit = async () => {
    const patch = {};
    if (title.trim() !== t.title) patch.title = title.trim();
    if ((description || "") !== (t.description || "")) patch.description = description;
    if ((dueDate || "") !== (t.due_date || "")) patch.due_date = dueDate || "";
    if ((startDate || "") !== (t.start_date || "")) patch.start_date = startDate || "";
    if (importance !== (t.importance || "medium")) patch.importance = importance;
    // Phase 84.2 — cross-project move + re-assign.
    if (projectId !== (t.project_id || "")) patch.project_id = projectId;
    if (canAssignOthers && assigneeId !== (t.assignee_id || "") && assigneeId !== "") {
      patch.assignee_id = assigneeId;
    }
    if (critical !== !!t.critical) patch.critical = critical;
    if (Object.keys(patch).length === 0) { setEditing(false); return; }
    await onPatch(t, patch);
    setEditing(false);
  };

  if (editing) {
    // Team dropdown always includes the current assignee so we don't accidentally
    // hide them (they might not be in `team` if they equal currentUserId).
    const teamOptions = [
      ...team,
      ...(t.assignee_id && !team.find((u) => u.id === t.assignee_id)
        ? [{ id: t.assignee_id, name: t.assignee_name || "Current assignee" }]
        : []),
    ];
    return (
      <div className="bg-white border border-[#F54800] rounded-sm p-3 space-y-2 shadow-sm" data-testid={`kanban-card-${t.id}-editing`}>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} className="h-8 rounded-sm text-sm" placeholder="Title" data-testid={`kanban-card-edit-title-${t.id}`} />
        <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} className="rounded-sm text-sm" placeholder="Description" data-testid={`kanban-card-edit-desc-${t.id}`} />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-overline text-[9px] text-slate-500 block mb-0.5">Start date</label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 rounded-sm text-sm" data-testid={`kanban-card-edit-start-${t.id}`} />
          </div>
          <div>
            <label className="label-overline text-[9px] text-slate-500 block mb-0.5">Due date</label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="h-8 rounded-sm text-sm" data-testid={`kanban-card-edit-due-${t.id}`} />
          </div>
        </div>
        <div>
          <label className="label-overline text-[9px] text-slate-500 block mb-0.5">Importance</label>
          <select
            value={importance}
            onChange={(e) => setImportance(e.target.value)}
            className={`h-8 w-full text-xs rounded-sm border px-2 font-medium ${IMPORTANCE_TONE[importance]}`}
            data-testid={`kanban-card-edit-importance-${t.id}`}
          >
            <option value="high">High</option>
            <option value="medium">Mid</option>
            <option value="low">Low</option>
          </select>
        </div>
        {/* Phase 84.2 — Move to project */}
        <div>
          <label className="label-overline text-[9px] text-slate-500 block mb-0.5">Project</label>
          <select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            className="h-8 w-full text-xs rounded-sm border border-slate-200 bg-white px-2"
            data-testid={`kanban-card-edit-project-${t.id}`}
          >
            <option value="">— None —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.business_unit ? ` · ${p.business_unit}` : ""}</option>
            ))}
            {/* Fallback if the current project isn't in the daily_task_sync list */}
            {t.project_id && !projects.find((p) => p.id === t.project_id) && (
              <option value={t.project_id}>{t.project_name || "Current project"} · (current)</option>
            )}
          </select>
        </div>
        {/* Phase 84.2 — Re-assign (admin/CM only) */}
        {canAssignOthers && (
          <div>
            <label className="label-overline text-[9px] text-slate-500 block mb-0.5">Assignee</label>
            <select
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="h-8 w-full text-xs rounded-sm border border-slate-200 bg-white px-2"
              data-testid={`kanban-card-edit-assignee-${t.id}`}
            >
              {teamOptions.map((u) => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center justify-between gap-1.5">
          <label className="inline-flex items-center gap-1.5 text-[11px] text-slate-600" data-testid={`kanban-card-edit-critical-${t.id}`}>
            <input type="checkbox" checked={critical} onChange={(e) => setCritical(e.target.checked)} className="accent-[#F54800]" />
            Critical (P0)
          </label>
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <Button size="sm" variant="outline" onClick={() => { setEditing(false); setTitle(t.title); setDescription(t.description || ""); setStartDate(t.start_date || ""); setDueDate(t.due_date || ""); setImportance(t.importance || "medium"); setProjectId(t.project_id || ""); setAssigneeId(t.assignee_id || ""); setCritical(!!t.critical); }} className="rounded-sm h-7 text-[11px]">Cancel</Button>
          <Button size="sm" onClick={saveEdit} className="rounded-sm h-7 text-[11px] bg-[#F54800] hover:bg-[#D63E00] text-white" data-testid={`kanban-card-save-${t.id}`}>Save</Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white border rounded-sm p-3 hover:shadow-sm transition-all group ${stale ? "border-red-300 bg-red-50/40" : "border-slate-200 hover:border-slate-300"}`} data-testid={`kanban-card-${t.id}`}>
      <div className="flex items-start gap-1.5">
        {done ? <CheckSquare size={13} className="text-emerald-600 mt-0.5 shrink-0" /> : null}
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-medium leading-snug break-words whitespace-pre-wrap ${done ? "line-through text-slate-400" : "text-slate-900"}`} data-testid={`kanban-card-title-${t.id}`}>
            {t.critical && <span className="text-[8px] font-bold px-1 py-0.5 rounded-sm bg-red-100 text-red-700 mr-1.5 align-middle" data-testid={`kanban-card-p0-${t.id}`}>P0</span>}
            {linkify(t.title)}
          </div>
        </div>
        {canInteract && (
          <button
            onClick={openEditor}
            className="text-slate-300 hover:text-slate-700 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            data-testid={`kanban-card-edit-${t.id}`}
            title="Edit · move · reassign"
          >
            <Pencil size={11} />
          </button>
        )}
        {canRemove && (
          <button
            onClick={() => onDelete(t)}
            className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
            data-testid={`kanban-card-delete-${t.id}`}
            title="Delete"
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      {t.description && (
        <div className="text-xs text-slate-600 mt-1 whitespace-pre-wrap break-words" data-testid={`kanban-card-desc-${t.id}`}>
          {linkify(t.description)}
        </div>
      )}
      <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-2 flex-wrap">
        {t.assignee_name && (
          <span
            className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm font-medium"
            data-testid={`kanban-card-assignee-${t.id}`}
            title={`Assigned to ${t.assignee_name}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#F54800]" aria-hidden="true" />
            {t.assignee_name}
          </span>
        )}
        <span className={`px-1 py-0.5 rounded-sm text-[9px] font-medium border ${IMPORTANCE_TONE[t.importance || "medium"]}`} data-testid={`kanban-card-importance-${t.id}`}>
          {(t.importance || "medium") === "medium" ? "Mid" : (t.importance === "high" ? "High" : "Low")}
        </span>
        {chip && (
          <span
            data-testid={`kanban-card-due-${t.id}`}
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium ${
              chip.tone === "red" ? "bg-red-50 text-red-700" :
              chip.tone === "orange" ? "bg-amber-50 text-amber-700" :
              "bg-slate-100 text-slate-600"
            }`}
          >
            {chip.tone === "red" ? <AlertTriangle size={9} /> : <Clock size={9} />} {chip.label}
          </span>
        )}
        {t.total_minutes > 0 && (
          <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[10px] font-medium bg-slate-100 text-slate-600" data-testid={`kanban-card-hours-${t.id}`} title="Total time logged">
            <Clock size={9} /> {formatLoggedHours(t.total_minutes)}
          </span>
        )}
        {columnStatus === "review" && <ReviewHistoryBadge history={t.review_history} taskId={t.id} />}
        {stale && (
          <span className="inline-flex items-center gap-0.5 text-[9px] px-1 py-0.5 rounded-sm bg-red-100 text-red-700 border border-red-300 font-semibold" data-testid={`kanban-card-stale-${t.id}`} title="Sitting in Review 3+ days without a decision">
            <AlertTriangle size={8} /> Stale review
          </span>
        )}
      </div>
      {/* Quick-move buttons */}
      {canInteract && (
        <div className="flex items-center gap-1 mt-2 flex-wrap" data-testid={`kanban-card-moves-${t.id}`}>
          {otherStatuses.map((s) => (
            <button
              key={s}
              onClick={() => onSetStatus(t, s)}
              className="text-[10px] px-1.5 py-0.5 rounded-sm bg-slate-100 text-slate-600 hover:bg-slate-200"
              data-testid={`kanban-card-move-${t.id}-${s}`}
              title={`Move to ${STATUS_LABEL[s]}`}
            >
              → {STATUS_LABEL[s]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Phase 101 — List view: same data, table layout, so the Board matches the
   Home Dashboard Task Hub's list mode field-for-field. */
function BoardListView({ tasks, currentUserId, isAdmin, canAssignOthers, isLeadOrFounder, onSetStatus, onDelete }) {
  if (tasks.length === 0) return null;
  return (
    <div className="overflow-x-auto" data-testid="project-board-list">
      <table className="w-full text-sm min-w-[760px]">
        <thead>
          <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-400">
            <th className="px-4 py-2 font-semibold">Task</th>
            <th className="px-3 py-2 font-semibold">Assignee</th>
            <th className="px-3 py-2 font-semibold">Importance</th>
            <th className="px-3 py-2 font-semibold">Start</th>
            <th className="px-3 py-2 font-semibold">Due</th>
            <th className="px-3 py-2 font-semibold">Hours</th>
            <th className="px-3 py-2 font-semibold">Status</th>
            <th className="px-3 py-2 font-semibold"></th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const canInteract = t.assignee_id === currentUserId || t.created_by === currentUserId || isAdmin;
            const canRemove = t.created_by === currentUserId || isAdmin;
            const chip = dueChip(t.due_date, t.status);
            const stale = t.status === "review" && isReviewStale(t);
            const options = DAILY_TASK_STATUSES.filter((s) => s !== "done" || isLeadOrFounder);
            return (
              <tr key={t.id} className={`border-b border-slate-50 last:border-0 hover:bg-slate-50/60 ${stale ? "bg-red-50/50" : ""}`} data-testid={`board-list-row-${t.id}`}>
                <td className="px-4 py-2 text-slate-800">
                  {t.critical && <span className="text-[8px] font-bold px-1 py-0.5 rounded-sm bg-red-100 text-red-700 mr-1.5 align-middle">P0</span>}
                  {t.title}
                  {t.review_history?.length > 0 && t.status === "review" && <ReviewHistoryBadge history={t.review_history} taskId={t.id} />}
                  {stale && <span className="ml-1.5 text-[9px] font-semibold text-red-700" data-testid={`board-list-stale-${t.id}`}>· Stale review</span>}
                </td>
                <td className="px-3 py-2 text-slate-600">{t.assignee_name || "—"}</td>
                <td className="px-3 py-2">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-medium border ${IMPORTANCE_TONE[t.importance || "medium"]}`}>
                    {(t.importance || "medium") === "medium" ? "Mid" : (t.importance === "high" ? "High" : "Low")}
                  </span>
                </td>
                <td className="px-3 py-2 text-slate-600 text-xs">{t.start_date || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {chip ? <span className={chip.tone === "red" ? "text-red-600 font-medium" : "text-slate-600"}>{t.due_date}</span> : (t.due_date || "—")}
                </td>
                <td className="px-3 py-2 text-slate-500 text-xs">{formatLoggedHours(t.total_minutes)}</td>
                <td className="px-3 py-2">
                  {canInteract ? (
                    <select
                      value={t.status}
                      onChange={(e) => onSetStatus(t, e.target.value)}
                      className="h-7 text-[11px] rounded-sm border border-slate-200 bg-white px-1"
                      data-testid={`board-list-status-${t.id}`}
                    >
                      {options.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  ) : (
                    <span className="text-xs text-slate-600">{STATUS_LABEL[t.status]}</span>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {canRemove && (
                    <button onClick={() => onDelete(t)} className="text-slate-300 hover:text-rose-600" data-testid={`board-list-delete-${t.id}`}>
                      <Trash2 size={12} />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
